import { Table, Tabs } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { getLang } from 'lowcomp/plugins/member/utils';
import React, { useState } from 'react';
import { useMemberTimeStore } from 'lowcode/utils/context/memberTimeZone';
import EfIcon from '@elem/ui/ef-icon';
import { TextLang } from '../../TextLang';
import css from './index.module.scss';
import { useGlobal } from '@/utils/useGlobal';
import { EFArrow } from '@/components/UI/svg/Search';
import { NoDataIcon } from '@/components/UI/svg/Ecocredits';
import { LevelLogItem, LevelType, PointsActivety, RewardsActivity } from '@/components/EcoCredits/type';
import { useLang } from '@/app/utils/useLang';
import { formatLocalTime, formatTargetLocalTime } from '@/utils/timeConversionTool';

interface props {
    locale: string;
    pointsActivity: PointsActivety[];
    rewardsActivity: RewardsActivity[];
    levelLogList: LevelLogItem[];
    openModal?: () => void;
    modal?: boolean;
    handleSortChange: (sortType: number | undefined) => void;
}

const NoDataComp: React.FC = () => {
    const { t } = useLang('ecocredits');
    return (
        <div className="no-content">
            <div className="no-content-icon">
                <NoDataIcon />
            </div>
            <p className="no-content-note">{t('no_history_note')}</p>
        </div>
    );
};

const PointsActivity: React.FC<props> = ({ locale, pointsActivity, rewardsActivity, levelLogList, openModal, modal, handleSortChange }) => {
    const { isMobile } = useGlobal();
    const { timezoneList } = useMemberTimeStore();
    const { t: eT } = useLang('ecocredits');
    const t = getLang(TextLang, locale);
    const [sortType, setSortType] = useState<number | undefined>(undefined);
    const statusTextMap: { [key: string]: string } = {
        FROZEN: t('pending'),
        REWARDED: t('received'),
        USED: t('deducted'),
        EXPIRED: t('deducted'),
        PENDING: t('Pending1'), // 等待
        ROLLBACK: t('rollback'), // 回滚
    };

    const typeMap: { [key: string]: string } = {
        COUPONS: t('discount_type'),
        THIRD_COUPONS: t('gift_card'),
        GIFT_CARD: t('gift_card'),
        FREE_PRODUCTS: t('product_text'),
    };

    const rewardTypeMap: { [key: string]: string } = {
        '1': t('redeemed'),
        '2': t('issued_tab_name'),
        '3': t('issued_tab_name'),
    };

    const levelTypeMap: Record<LevelType, string> = {
        [LevelType.HISTORY_REFRESH_MEMBER]: t('history_refresh_member'),
        [LevelType.HISTORY_REFRESH_PLUS_MEMBER]: t('history_refresh_plus_member'),
        [LevelType.REGISTER]: t('register'),
        [LevelType.UPGRADE]: t('upgrade'),
        [LevelType.DEMOTION]: t('demotion'),
        [LevelType.RELEGATION]: t('relegation'),
    };

    const sourceText: any = {
        COMMUNITY_SEND_CONTENT: eT('post'),
        COMMUNITY_SEND_COMMENT: eT('comment'),
        COMMUNITY_UPVOTE: eT('upvote'),
        TIME_LIMIT_FIRST_LOGIN: eT('first_login'),
    };

    const getAction = (record: PointsActivety) => {
        const { source, source2, ruleType, couponName, goodTitle } = record;
        // 第三方优惠券
        if (ruleType === 'THIRD_COUPONS') {
            return t('gift_card');
        }

        // 实体产品
        if (ruleType === 'FREE_PRODUCTS') {
            return t('product_text');
        }
        if (sourceText[source]) {
            return sourceText[source];
        }
        // 常规
        if (source === 'REGISTER' || source === 'ACCOUNT_BIND') return t('registered'); // 注册
        if (source === 'WARRANTY') return t('warranty_capital_letters'); // 保修
        if (source === 'EXPIRED') return t('expired'); // 逾期
        if (source?.startsWith('SHOP_')) return t('purchase'); // 购买
        if (source === 'REFUNDED') return t('refund'); // 退款消耗
        if (source === 'SUBSCRIBE') return t('subscribe_capital_letters'); // 订阅
        if (source?.startsWith('GIFT_CARD')) return t('gift_card'); // 礼品卡
        if (source?.startsWith('COUPONS_')) return t('discount'); // 折扣劵
        if (source === 'MANUAL_PLUS') return t('manually_added'); // 手动添加
        if (source === 'MANUAL_MINUS') return t('manually_deducted'); // 手动减少
        if (source === 'EDM') return t('subscribe_capital_letters'); // 手动减少
        if (source === 'POINTS_CASH') return t('points_cash'); // 积分抵扣

        // 活动
        if (source === 'DRAW_PLUS') return t('event_action_text'); // 参加活动获得积分
        if (source === 'DRAW_MINUS') return t('event_action_text'); // 参加活动扣减积分
        if (source === 'CREDITS_DAY_MINUS') return t('event_action_text'); // 参加活动扣减积分
        if (source === 'WARRANTY_MINUS') return t('warranty_minus_action_text'); // 保修扣除积分
        if (source === 'QUESTION_REWARD') return t('question_correctly'); // 答题获取积分
        if (source === 'REFERRAL_PLAN') return t('referral_correctly'); // 分享积分
        if (source === 'ATTENTION_YOUTUBE') return t('follow_youtube_text');
        if (source === 'ATTENTION_INSTAGRAM') return t('follow_instagram_text');
        if (source === 'ATTENTION_X') return t('follow_x_text');
        if (source === 'ATTENTION_FACEBOOK') return t('follow_facebook_text');
        if (source === 'JOIN_FACEBOOK') return t('join_facebook_text');
        if (source === 'ATTENTION_LINE') return t('follow_line_text');
        if (source === 'ATTENTION_KAKOO') return t('follow_kakoo_text');
        if (source === 'WATT_A_YEAR2024') return t('wattayear_2024');
        // 2025会员节新增 FIRST_LOGIN("首次登录"), Y25_KNOW_BRAND("2025年会员节了解品牌"), Y25_KNOW_MEMBER("2025年会员节了解会员"), Y25_SHARE_STORY("2025年会员节分享故事"), FIRST_ORDER("首单"), REPEAT_ORDER("复购")
        if (source === 'FIRST_LOGIN') return t('first_login_2025');
        if (source === 'Y25_KNOW_BRAND') return t('know_brand_2025');
        if (source === 'Y25_KNOW_MEMBER') return t('know_member_2025');
        if (source === 'Y25_SHARE_STORY') return t('share_story_2025');
        if (source === 'FIRST_ORDER') return t('first_order_2025');
        if (source === 'REPEAT_ORDER') return t('repeat_order_2025');

        // 打卡
        if (['NORMAL_SIGN_IN', 'REISSUE_SIGN_IN', 'DURATION_SIGN_IN', 'ANY_SIGN_IN', 'REISSUE_SIGN_IN_CONSUME'].includes(source)) return t('clock_in');

        // 个人中心细分
        if (source === 'ACCOUNT_INFO') {
            if (source2 === 'ACCOUNT_INFO_BIRTHDAY') {
                return t('profile_birthday');
            }
            if (source2 === 'ACCOUNT_INFO_ADDRESS') {
                return t('profile_address');
            }
            if (source2 === 'ACCOUNT_INFO_NICKNAME') {
                return t('profile_username');
            }
            if (source2 === 'ACCOUNT_INFO_ICON') {
                return t('profile_picture');
            }
            if (source2 === 'ACCOUNT_INFO_NAME') {
                return t('profile_contact');
            }
            return t('profile_a');
        }
        // app浏览
        if (source === 'APP_EXPLORE') {
            if (source2 === 'APP_EXPLORE_FIRST') {
                return t('View_1_Explore_Article');
            }
            if (source2 === 'APP_EXPLORE_SECOND') {
                return t('View_2_Explore_Article');
            }
            if (source2 === 'APP_EXPLORE_THIRD') {
                return t('View_3_Explore_Article');
            }
        }
        if (source === 'APP_ENABLE_PROMOTION') return t('Enable_push_notifications');

        if (source === 'BKW_SAVING_CALCULATOR') return t('Try_the_energy_calculator_once');

        return source;
    };

    const onSortTime = () => {
        if (sortType === undefined) {
            setSortType(1);
            handleSortChange(1);
            return;
        }
        if (sortType !== undefined) {
            if (sortType === 1) {
                setSortType(2);
                handleSortChange(2);
            } else {
                setSortType(1);
                handleSortChange(1);
            }
        }
    };

    const pointsColumns: ColumnsType<PointsActivety> = [
        { title: t('action'), dataIndex: 'source', width: 142, render: (_v, record) => getAction(record) },
        { title: t('points_capital_letters'), dataIndex: 'points', width: 141, className: 'color' },
        { title: t('status'), dataIndex: 'status', width: 168, className: 'color', render: (value) => statusTextMap[String(value)] || value },
        {
            title: t('date_of_event'),
            dataIndex: 'createTime',
            width: 235,
            render(_v, record) {
                const { createTimeStamp, createTime } = record;
                if (createTimeStamp) return formatLocalTime(createTimeStamp, timezoneList);
                return formatTargetLocalTime(createTime, timezoneList) || '';
            },
        },
        {
            title: () => (
                <span className="expiration-date-title">
                    {t('expiration_date')}
                    <span className="sort-icon" onClick={() => onSortTime()}>
                        <EfIcon pc="https://websiteoss.ecoflow.com/upload/image/1740626857153/switch.svg" />
                    </span>
                </span>
            ),
            dataIndex: 'validEndTime',
            width: 235,
            render(_v, record) {
                if (record.status === 'EXPIRED' || record.status === 'ROLLBACK' || record.status === 'FROZEN') {
                    return '-';
                }
                const { validEndTime, validStartTime, createTimeStamp, createTime } = record;

                if (record.status === 'USED') {
                    if (createTimeStamp) return formatLocalTime(createTimeStamp, timezoneList);
                    return formatTargetLocalTime(createTime, timezoneList) || '-';
                }
                return formatTargetLocalTime(validEndTime as string, timezoneList) || '-';
            },
        },
    ];

    const rewardsColumns: ColumnsType<RewardsActivity> = [
        {
            title: t('date'),
            dataIndex: 'createTime',
            width: 235,
            render(_v, record) {
                const { createTime } = record;
                return createTime ? formatLocalTime(createTime, timezoneList) : '';
            },
        },
        {
            title: t('action'),
            dataIndex: 'rewardType',
            width: 142,
            render: (value, record) => {
                if (record?.useStatus === 'USED') {
                    return t('used');
                }
                return rewardTypeMap[String(value)] || '-';
            },
        },
        {
            title: t('rewards_title'),
            width: 141,
            className: 'color',
            render: (_, record) => {
                const { ruleType, currencySymbol, amount, discountType, couponName, goodTitle, variantTitle, prizeName } = record;
                // 抽奖
                if (prizeName) {
                    return prizeName;
                }

                // 存在couponName字段
                if (couponName) {
                    return couponName;
                }

                // 第三方优惠券
                if (ruleType === 'THIRD_COUPONS') {
                    return couponName || '-';
                }

                // 实体产品
                if (ruleType === 'FREE_PRODUCTS') {
                    return variantTitle ? `${goodTitle}-${variantTitle}` : goodTitle;
                }

                return `${typeMap[String(ruleType)] || ruleType} ${amount}${discountType === 'percentage' ? '%' : currencySymbol}`;
            },
        },
        { title: t('type_title'), dataIndex: 'ruleType', width: 168, className: 'color', render: (value) => typeMap[String(value)] || value },
    ];

    const levelLogColumns: ColumnsType<LevelLogItem> = [
        { title: t('action'), dataIndex: 'type', width: 225, render: (value: LevelType) => levelTypeMap[value] },
        {
            title: t('date'),
            dataIndex: 'createTime',
            width: 142,
            render(_v, record) {
                return record.createTime ? formatTargetLocalTime(record.createTime, timezoneList) : '';
            },
        },
        {
            title: t('deadline'),
            dataIndex: 'validEndTime',
            width: 142,
            render(_v, record) {
                return record.validEndTime ? formatTargetLocalTime(record.validEndTime, timezoneList) : '-';
            },
        },
    ];

    const Footer = () => (
        <div className="tabel-footer">
            <span onClick={openModal}>
                <span className="more-text">{t('view_more')}</span>
                <EFArrow />
            </span>
        </div>
    );

    return (
        <div className={`${css.points_activity} ${modal ? 'modal' : ''}`}>
            <h1 className="history-title" id="FOR-POINTS-ACTIVELY">
                {t('points_activity_a')}
            </h1>

            <Tabs type="card" className="rewards-tabs" centered={true}>
                <Tabs.TabPane tab={t('points_tab_name')} key="points">
                    {!!pointsActivity?.length ? (
                        <>
                            <Table
                                scroll={{ x: true }}
                                columns={pointsColumns}
                                dataSource={modal ? pointsActivity : pointsActivity?.slice(0, 5)}
                                rowKey="recordId"
                                className="history-tabel"
                                rowClassName={(row, i) => `${i % 2 === 1 ? 'odd' : 'even'} ${row?.status}`}
                                pagination={false}
                                bordered={false}
                                footer={modal ? undefined : () => <Footer />}
                            />
                            <p className="history-note">{t('history_note')}</p>
                        </>
                    ) : (
                        <NoDataComp />
                    )}
                </Tabs.TabPane>
                <Tabs.TabPane tab={t('rewards_tab_name')} key="rewards">
                    {!!rewardsActivity?.length ? (
                        <>
                            <Table
                                columns={rewardsColumns}
                                dataSource={modal ? rewardsActivity : rewardsActivity?.slice(0, 5)}
                                rowKey="id"
                                className="history-tabel"
                                rowClassName={(row, i) => `${i % 2 === 1 ? 'odd' : 'even'}`}
                                pagination={false}
                                bordered={false}
                                footer={modal ? undefined : () => <Footer />}
                            />
                            <p className="history-note">{t('history_note')}</p>
                        </>
                    ) : (
                        <NoDataComp />
                    )}
                </Tabs.TabPane>
                <Tabs.TabPane tab={t('level_change_log')} key="level">
                    {!!levelLogList?.length ? (
                        <>
                            <Table
                                columns={levelLogColumns}
                                dataSource={levelLogList}
                                rowKey="id"
                                className="history-tabel"
                                rowClassName={(row, i) => `${i % 2 === 1 ? 'odd' : 'even'}`}
                                pagination={false}
                                bordered={false}
                                footer={modal ? undefined : () => <Footer />}
                            />
                        </>
                    ) : (
                        <NoDataComp />
                    )}
                </Tabs.TabPane>
            </Tabs>
        </div>
    );
};

export default PointsActivity;
